import { useEffect, useRef, useState } from 'react';
import { api, type VisaDocumentKind, type VisaDocumentMetadata } from './api';
import { VIZE_BELGELERI as M } from './metin/vize-belgeleri';

const names: Record<VisaDocumentKind, string> = { PASSPORT: 'pasaport', BIOMETRIC: 'biyometrik-fotograf', HOTEL_BOOKING: 'otel-rezervasyonu', FLIGHT_TICKET: 'ucak-bileti', VISA_PDF: 'vize-belgesi', INSURANCE_POLICY: 'sigorta-policesi' };
const extensions: Record<string, string> = { 'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };
function applicantFileName(name: string) {
  const safe = name.replace(/ı/g, 'i').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
  return `${safe || 'basvuru'}-vize-belgeleri.zip`;
}
function zipFiles(files: { name: string; blob: Blob }[], signal: AbortSignal): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./turPdf/zip.worker.ts', import.meta.url), { type: 'module' });
    const cleanup = () => { worker.terminate(); signal.removeEventListener('abort', abort); };
    const abort = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) { abort(); return; }
    worker.onmessage = (event: MessageEvent<{ blob?: Blob; error?: boolean }>) => { cleanup(); if (event.data.blob && !event.data.error) resolve(event.data.blob); else reject(new Error('ZIP unavailable')); };
    worker.onerror = () => { cleanup(); reject(new Error('ZIP unavailable')); };
    try { worker.postMessage(files); } catch { cleanup(); reject(new Error('ZIP unavailable')); }
  });
}
export function VisaBulkDownload({ requestId, applicantName, documents }: { requestId: string; applicantName: string; documents: VisaDocumentMetadata[] }) {
  const controller = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(0);
  const uploaded = documents.filter(document => document.uploaded);
  useEffect(() => () => { generation.current++; controller.current?.abort(); }, [requestId]);
  const downloadAll = async () => {
    if (controller.current || uploaded.length === 0) return;
    const abort = new AbortController(); controller.current = abort;
    const current = ++generation.current;
    const active = () => generation.current === current && !abort.signal.aborted;
    setBusy(true); setFailed(false); setDone(0);
    try {
      if (uploaded.length > 50 || new Set(uploaded.map(document => document.id)).size !== uploaded.length) throw new Error('Invalid document set');
      const files: { name: string; blob: Blob }[] = [];
      const counts: Partial<Record<VisaDocumentKind, number>> = {};
      // Sequential reads bound provider load and preserve the selected case's immutable batch.
      for (const document of uploaded) {
        if (!active()) return;
        if (!document.id || !names[document.kind]) throw new Error('Missing document metadata');
        const blob = await api.previewVisaDocument(requestId, document.id, abort.signal);
        if (!active()) return;
        const extension = extensions[blob.type]; if (!extension) throw new Error('Invalid MIME');
        const count = (counts[document.kind] ?? 0) + 1; counts[document.kind] = count;
        files.push({ name: `${names[document.kind]}${count > 1 ? `-${count}` : ''}.${extension}`, blob });
        setDone(files.length);
      }
      const zip = await zipFiles(files, abort.signal);
      if (!active()) return;
      const url = URL.createObjectURL(zip);
      try {
        const anchor = document.createElement('a'); anchor.href = url; anchor.download = applicantFileName(applicantName);
        document.body.append(anchor); anchor.click(); anchor.remove();
      } finally { URL.revokeObjectURL(url); }
    } catch { if (active()) setFailed(true); }
    finally { if (generation.current === current) { controller.current = null; setBusy(false); } }
  };
  if (uploaded.length === 0) return null;
  return <div className="visa-bulk-download" data-testid="visa-bulk-download">
    <button type="button" disabled={busy} onClick={() => void downloadAll()}>{busy ? M.topluBekle : M.topluIndir}</button>
    {busy ? <><span role="status">{M.topluIlerleme(done, uploaded.length)}</span><button type="button" onClick={() => controller.current?.abort()}>{M.topluIptal}</button></> : null}
    {failed ? <p role="alert">{M.topluHata}</p> : null}
  </div>;
}
